import { useEffect, useState } from 'react';
import { GoabButton } from '@abgov/react-components';

import './Notes.scss';
import { NoteItem } from '../../../../../common/types';
import { wdpUtil } from '../../../../../common/utils/util';
import { createFieldTestId, createButtonTestId, createPageTestId } from '../../../../../common/utils/testIds';

interface NotesProps {
  updateFlow: boolean;
  canShowEdit?: boolean;
  maxLength?: number;
  readOnly?: boolean;
  notes?: NoteItem[];
  isLoading?: boolean;
  isError?: boolean;
  activeSection?: string | null;
  isBusy?: boolean;
  onChange?: (changeType: string, change: string[] | boolean | null) => void;
}

export function Notes(props: NotesProps) {
  const maxLength = props.maxLength ?? 1200;
  const readOnly = props.readOnly ?? false;
  const isUpdateFlow = props.updateFlow;
  const notes = props.notes ?? [];
  const isLoading = props.isLoading ?? false;
  const isError = props.isError ?? false;
  const canShowEdit = props.canShowEdit ?? false;
  const isBusy = props.isBusy ?? false;
  const [newNoteContent, setNewNoteContent] = useState('');

  // Sort notes in reverse chronological order (newest first)
  const sortedNotes = [...notes].sort((a, b) => {
    return new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime();
  });

  /***************************************************************************************
   * handleAddNote
   * Saves the current textarea content as a note immediately via the parent's 'save' handler.
   ****************************************************************************************/
  const handleAddNote = () => {
    const trimmedContent = newNoteContent.trim();
    if (trimmedContent) {
      setNewNoteContent('');
      props.onChange?.('save', [trimmedContent]);
    }
  };

  const remainingChars = maxLength - newNoteContent.length;
  const isCharLimitReached = remainingChars <= 0;
  const isSectionDirty = newNoteContent.trim().length > 0;

  useEffect(() => {
    if (props.onChange) {
      props.onChange('dirty', isSectionDirty);
    }
  }, [isSectionDirty, props.onChange]);

  return (
    <section className="notes-section" data-testid={createPageTestId('notes')}>
      {!readOnly && isUpdateFlow && canShowEdit && (
        <div className="header-and-actions">
          <h4 data-testid={createFieldTestId('note-label')}>Add a note</h4>
        </div>
      )}

      {!readOnly && isUpdateFlow && canShowEdit && (
        <div className="add-note-section" data-testid={createPageTestId('add-note-section')}>
          
          <div className="textarea-wrapper" data-testid={createFieldTestId('note-textarea-wrapper')}>
            <textarea
              id="add-note-textarea"
              data-testid={createFieldTestId('note-textarea')}
              className="note-textarea"
              value={newNoteContent}
              onChange={(e) => {
                if (e.target.value.length <= maxLength) {
                  setNewNoteContent(e.target.value);
                }
              }}
              maxLength={maxLength}
              placeholder="Enter your note here..."
              rows={4}
              disabled={!isUpdateFlow}
            />
            <div className="character-counter" data-testid={createFieldTestId('note-char-counter')} style={{ color: remainingChars < 50 ? '#d32f2f' : '#666' }}>
              {remainingChars} characters remaining
            </div>
          </div>
          <GoabButton 
            data-testid={createButtonTestId('add-note')}
            type="primary" 
            size='compact'
            onClick={handleAddNote}
            disabled={!newNoteContent.trim() || isCharLimitReached || isBusy}
          >
            Add note
          </GoabButton>
        </div>
      )}

      <div className="notes-content">
      <h3 className="notes-count-label" data-testid={createPageTestId('notes-count')}>Notes ({notes.length})</h3>

      <div className="notes-list" data-testid={createPageTestId('notes-list')}>
        {/* Saved notes from backend */}
        {isLoading ? (
          <p className="no-notes-message" data-testid={createPageTestId('notes-loading')}>Loading notes...</p>
        ) : isError ? (
          <p className="no-notes-message" data-testid={createPageTestId('notes-error')} style={{ color: '#d32f2f' }}>Failed to load notes</p>
        ) : sortedNotes.length === 0 ? (
          <p className="no-notes-message" data-testid={createPageTestId('notes-empty')}>No notes available</p>
        ) : (
          sortedNotes.map((note) => (
            <div key={note.id} className="note-item" data-testid={createPageTestId(`note-${note.id}`)}>
              <div className="note-header" data-testid={createPageTestId(`note-header-${note.id}`)}>
                <span className="note-author" data-testid={createPageTestId(`note-author-${note.id}`)}>
                  {note.createdByDisplayName}
                </span>
                <span className="note-timestamp" data-testid={createPageTestId(`note-timestamp-${note.id}`)}>
                  {wdpUtil.formatNoteTimestamp(note.createdAtUtc)}
                </span>
              </div>
              <div className="note-content" data-testid={createPageTestId(`note-content-${note.id}`)}>
                {note.body}
              </div>
            </div>
          ))
        )}
      </div>
      </div>
    </section>
  );
}